import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData, QueryKey } from '@tanstack/react-query';
import { Supplier, SuppliersResponse, SupplierStatusTotals } from '@app-types';

interface UseSupplierPageDataOptions {
  queryKey: QueryKey;
  queryFn: () => Promise<SuppliersResponse>;
  pageSize: number; // Required for correct totalPages calculation
  enabled?: boolean;
}

export interface UseSupplierPageDataReturn {
  // Data
  suppliers: Supplier[];
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;

  // Response
  response: SuppliersResponse | undefined;

  // Status totals
  filterCounts: Record<string, number>;

  // Pagination
  totalItems: number;
  totalPages: number;

  // Actions
  refreshData: () => void;
}

/**
 * Generic hook for data fetching in supplier pages
 * Handles: data fetching, statusTotals preservation, filter counts, pagination
 * Same implementation as useSchedulePageData, useAssessmentPageData, useContractPageData
 */
export function useSupplierPageData(options: UseSupplierPageDataOptions): UseSupplierPageDataReturn {
  const { queryKey, queryFn, pageSize, enabled = true } = options;
  const queryClient = useQueryClient();

  // Fetch data with placeholderData to prevent UI flicker
  const {
    data: response,
    isLoading,
    error,
    isFetching
  } = useQuery<SuppliersResponse>({
    queryKey,
    queryFn,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
    enabled
  });

  // Extract suppliers
  const suppliers = useMemo(() => response?.suppliers || [], [response]);

  // Preserve last known totals to avoid counter flicker between filter changes
  const [lastStatusTotals, setLastStatusTotals] = useState<SupplierStatusTotals | null>(null);
  useEffect(() => {
    if (response?.statusTotals) {
      setLastStatusTotals(response.statusTotals);
    }
  }, [response?.statusTotals]);

  // Calculate filter counts from statusTotals
  const filterCounts: Record<string, number> = useMemo(() => {
    const totals = response?.statusTotals || lastStatusTotals;

    if (!totals) {
      return {
        all: response?.total || 0,
        Active: 0,
        Inactive: 0,
        Pending: 0,
        Deleted: 0
      };
    }

    const allFromTotals = (totals.Active || 0) + (totals.Inactive || 0) + (totals.Pending || 0);

    return {
      all: allFromTotals || response?.total || 0,
      Active: totals.Active || 0,
      Inactive: totals.Inactive || 0,
      Pending: totals.Pending || 0,
      Deleted: totals.Deleted || 0
    };
  }, [response, lastStatusTotals]);

  // Pagination calculations
  const totalItems = response?.total || 0;
  const totalPages = Math.ceil(totalItems / (pageSize || 1));

  // Refresh data callback
  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [queryKey[0]] });
  }, [queryClient, queryKey]);

  return {
    // Data
    suppliers,
    isLoading,
    error: error as Error | null,
    isFetching,

    // Response
    response,

    // Status totals
    filterCounts,

    // Pagination
    totalItems,
    totalPages,

    // Actions
    refreshData
  };
}
